/*
 * Sep 24 2026 — asking for the app's own file, on his link.
 *
 * The file is about 550 KB on the wire. On the slowest links measured (4 KB/s from Pavan's
 * desk on Sep 24, 5 to 8 KB/s on his own) it takes one to two minutes, and webpack gives a
 * file 120 seconds before it calls it failed, while the browser is still downloading it. So
 * a load that was going to arrive was told "could not finish loading". A second ask for the
 * same file joins the download the browser already has in hand rather than starting over,
 * so a file that timed out is asked for again, up to three times in all; the page's boot
 * state (public/index.html) keeps showing the time so far meanwhile. Only a file that still
 * has not come turns the boot state into the failed lamp, with Try again.
 *
 * This file rides in the page's own script, so it stays small and imports nothing.
 */

export const BOOT_TRIES = 3;
export const RETRY_AFTER_ERROR_MS = 3000;

function chunkFailed(e: any): boolean {
  if (!e) return false;
  if (e.name === 'ChunkLoadError' || e.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  return /Loading (CSS )?chunk \S+ failed/i.test(String(e.message || ''));
}

export function loadTheApp(
  load: () => Promise<unknown>,
  tries: number = BOOT_TRIES,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<void> {
  return load().then(
    () => undefined,
    (e: any) => {
      if (tries > 1 && chunkFailed(e)) {
        // A timeout is a download still under way: ask again at once. A network error is a
        // dropped connection: give it a moment to come back first.
        const pause = e && e.type === 'timeout' ? Promise.resolve() : wait(RETRY_AFTER_ERROR_MS);
        return pause.then(() => loadTheApp(load, tries - 1, wait));
      }
      showBootFailed();
      return undefined;
    },
  );
}

export function showBootFailed(doc: Document = document): void {
  const boot = doc.getElementById('boot');
  if (!boot) {
    // No boot state: the app itself has already painted (its crash screen answers for
    // anything after that), or an old page. Say it in plain words only on an empty root.
    const root = doc.getElementById('root');
    if (!root || root.childElementCount) return;
    const p = doc.createElement('p');
    p.setAttribute('role', 'alert');
    p.textContent = 'On Edge could not finish loading. Check the connection, then reload the page.';
    p.style.cssText = 'font:16px/1.6 -apple-system,BlinkMacSystemFont,system-ui,sans-serif;color:#0C1943;padding:40px 16px;margin:0;max-width:34em';
    root.appendChild(p);
    return;
  }
  boot.className = 'is-failed';
  boot.removeAttribute('aria-busy');
  const lamp = boot.querySelector('.boot-lamp');
  if (lamp) lamp.setAttribute('role', 'alert');
  const what = doc.getElementById('boot-what');
  if (what) what.textContent = 'On Edge could not finish loading';
  const say = doc.getElementById('boot-say');
  if (say) say.textContent = 'Check the connection, then press Try again.';
  if (lamp && !lamp.querySelector('.boot-key')) {
    const again = doc.createElement('button');
    again.type = 'button';
    again.className = 'boot-key';
    again.textContent = 'Try again';
    again.addEventListener('click', () => { doc.defaultView?.location.reload(); });
    lamp.appendChild(again);
    again.focus();
  }
}
